/**
 * `telegram/useTelegramTheme.ts` — the member's Telegram theme on Tarii's
 * design tokens (#2074; design pass #2071).
 *
 * Telegram hands the Mini App its colours (`themeParams`) and whether the
 * member runs light or dark (`colorScheme`). The app follows both: the base
 * theme flips to a Tarii light or dark set (`data-theme`, never persisted —
 * the member's own web theme is untouched), then Telegram's colours are laid
 * over the `--color-*` tokens inline, so every screen reads like part of
 * Telegram. Re-applied on `themeChanged`.
 *
 * The map (`tokensForTheme`), key by key:
 *
 *   bg_color                → bg (the page)
 *   section_bg_color        → bg-elevated, surface (cards, sheets, tab bar)
 *   secondary_bg_color      → bg-subtle (hover rows, quiet fills)
 *   text_color              → fg; fg-muted is text eased toward the hint
 *   subtitle_text_color     → fg-subtle (secondary lines; hint when absent)
 *   hint_color              → fg-faint; border-strong is bg eased toward it
 *   section_separator_color → border (bg eased toward hint when absent)
 *   button_color            → accent, accent-strong; accent-soft is a tint of
 *                             it on the section colour
 *   button_text_color       → fg-on-accent
 *   link_color              → accent-fg (links are accent text in Tarii)
 *   accent_text_color       → accent-fg when there is no link colour
 *   destructive_text_color  → danger, when it holds AA on the section colour
 *   header_bg_color         → not a token: Telegram paints its own header,
 *                             and the app asks it to use bg_color so its bar
 *                             and the app's top row read as one
 *
 * The soft fills behind chips (success / warning / danger / pink) are
 * re-tinted on Telegram's section colour from the base theme's own hues, so
 * no chip keeps a background tuned for Tarii's surfaces. Light Telegram sits
 * on Still water and dark Telegram on Still water night (#2496: bright,
 * AA-checked hues for dark surfaces, and the same Hanken Grotesk face on
 * both), never on Slate Dark's indigo. From the base the app also keeps what
 * Telegram does not set: success and warning, the radii, and the glass,
 * sky and button-shadow tokens. `--glass-fill` and `--shadow-button` read the
 * colour tokens through `var()`, so the glass takes Telegram's section colour
 * and the button shadow takes Telegram's button colour with no extra code.
 */
import { useEffect } from "react";

import { type Theme, applyTheme } from "../lib/theme";
import {
  type TelegramThemeParams,
  type TelegramWebApp,
  launchParams,
  telegramWebApp,
  whenTelegramWebApp,
} from "./telegramSdk";

type Rgb = [number, number, number];

/** The base themes under Telegram's colours. */
export const TELEGRAM_LIGHT_BASE: Theme = "still-water";
export const TELEGRAM_DARK_BASE: Theme = "still-water-night";

function rgb(hex: string | undefined): Rgb | null {
  if (!hex) return null;
  const m = /^#?([0-9a-f]{2})([0-9a-f]{2})([0-9a-f]{2})$/i.exec(hex.trim());
  if (!m) return null;
  return [
    Number.parseInt(m[1] ?? "0", 16),
    Number.parseInt(m[2] ?? "0", 16),
    Number.parseInt(m[3] ?? "0", 16),
  ];
}

/** A token value ("12 42 42") back to RGB. */
function tripleRgb(value: string): Rgb | null {
  const parts = value.trim().split(/\s+/).map(Number);
  if (parts.length !== 3 || parts.some((n) => !Number.isFinite(n))) return null;
  return parts as Rgb;
}

function mix(a: Rgb, b: Rgb, t: number): Rgb {
  return [0, 1, 2].map((i) => Math.round((a[i] ?? 0) + ((b[i] ?? 0) - (a[i] ?? 0)) * t)) as Rgb;
}

const triple = (c: Rgb) => `${c[0]} ${c[1]} ${c[2]}`;

const isDarkRgb = (c: Rgb) => c[0] + c[1] + c[2] < 3 * 96;

function luminance(c: Rgb): number {
  const [r, g, b] = c.map((v) => {
    const s = v / 255;
    return s <= 0.03928 ? s / 12.92 : ((s + 0.055) / 1.055) ** 2.4;
  }) as Rgb;
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}

/** WCAG contrast ratio between two colours. */
export function contrast(a: Rgb, b: Rgb): number {
  const [hi, lo] = [luminance(a), luminance(b)].sort((x, y) => y - x) as [number, number];
  return (hi + 0.05) / (lo + 0.05);
}

/** The chip hues whose soft fills are re-tinted on Telegram's surfaces. */
export const SOFT_HUES = ["success", "warning", "danger", "pink"] as const;
export type SoftHue = (typeof SOFT_HUES)[number];

/**
 * Telegram's colours → `--color-*` token values (space-separated RGB).
 * `hues` are the base theme's own semantic colours (read from the page), used
 * to tint the soft chip fills on Telegram's section colour.
 */
export function tokensForTheme(
  theme: TelegramThemeParams,
  hues: Partial<Record<SoftHue, Rgb>> = {},
): Record<string, string> {
  const out: Record<string, string> = {};
  const bg = rgb(theme.bg_color);
  const secondary = rgb(theme.secondary_bg_color);
  const section = rgb(theme.section_bg_color) ?? bg;
  const text = rgb(theme.text_color);
  const hint = rgb(theme.hint_color);
  const subtitle = rgb(theme.subtitle_text_color) ?? hint;
  const link = rgb(theme.link_color) ?? rgb(theme.accent_text_color);
  const button = rgb(theme.button_color);
  const buttonText = rgb(theme.button_text_color);
  const separator = rgb(theme.section_separator_color);
  // Telegram's red carries error text, so it lands only where it reads (AA
  // on the section colour) — iOS's light #ff3b30 on white does not, and the
  // base theme's red stays.
  const telegramRed = rgb(theme.destructive_text_color);
  const destructive =
    telegramRed && (!section || contrast(telegramRed, section) >= 4.5) ? telegramRed : null;
  // Soft fills: a light tint on light surfaces (the chip's text keeps at
  // least the base theme's contrast), a stronger one on dark surfaces so the
  // chip still reads as a chip on Telegram's dark grey.
  const tint = section && isDarkRgb(section) ? 0.16 : 0.1;

  if (bg) out["--color-bg"] = triple(bg);
  if (section) {
    out["--color-bg-elevated"] = triple(section);
    out["--color-surface"] = triple(section);
  }
  if (secondary) out["--color-bg-subtle"] = triple(secondary);
  else if (bg && hint) out["--color-bg-subtle"] = triple(mix(bg, hint, 0.12));
  if (text) {
    out["--color-fg"] = triple(text);
    out["--color-fg-muted"] = triple(hint ? mix(text, hint, 0.35) : text);
  }
  if (subtitle) out["--color-fg-subtle"] = triple(subtitle);
  if (hint) out["--color-fg-faint"] = triple(hint);
  if (separator) out["--color-border"] = triple(separator);
  else if (bg && hint) out["--color-border"] = triple(mix(bg, hint, 0.25));
  if (bg && hint) out["--color-border-strong"] = triple(mix(bg, hint, 0.5));
  if (button) {
    out["--color-accent"] = triple(button);
    out["--color-accent-strong"] = triple(button);
    // The accent tint (today, the selected day) stays a touch stronger.
    if (section) out["--color-accent-soft"] = triple(mix(section, button, tint * 1.4));
  }
  if (link) out["--color-accent-fg"] = triple(link);
  if (buttonText) out["--color-fg-on-accent"] = triple(buttonText);
  if (destructive) out["--color-danger"] = triple(destructive);
  if (section) {
    for (const hue of SOFT_HUES) {
      const colour = hue === "danger" ? (destructive ?? hues.danger) : hues[hue];
      if (colour) out[`--color-${hue}-soft`] = triple(mix(section, colour, tint));
    }
  }
  return out;
}

// Every property this module may set inline — cleared before each apply so a
// theme change never keeps a colour the new theme does not give.
const TOKEN_NAMES = [
  "--color-bg",
  "--color-bg-elevated",
  "--color-surface",
  "--color-bg-subtle",
  "--color-fg",
  "--color-fg-muted",
  "--color-fg-subtle",
  "--color-fg-faint",
  "--color-border",
  "--color-border-strong",
  "--color-accent",
  "--color-accent-strong",
  "--color-accent-soft",
  "--color-accent-fg",
  "--color-fg-on-accent",
  "--color-danger",
  ...SOFT_HUES.map((hue) => `--color-${hue}-soft`),
];

/** The base theme's own chip hues, as the page computes them now. */
function readBaseHues(): Partial<Record<SoftHue, Rgb>> {
  const out: Partial<Record<SoftHue, Rgb>> = {};
  if (typeof window === "undefined" || typeof window.getComputedStyle !== "function") return out;
  const style = window.getComputedStyle(document.documentElement);
  for (const hue of SOFT_HUES) {
    const value = tripleRgb(style.getPropertyValue(`--color-${hue}`));
    if (value) out[hue] = value;
  }
  return out;
}

function applyTelegramTheme(app: TelegramWebApp | null): void {
  if (typeof document === "undefined") return;
  const launch = launchParams();
  const theme = app?.themeParams ?? launch.themeParams ?? {};
  const scheme = app?.colorScheme ?? launch.colorScheme;
  const bg = rgb(theme.bg_color);
  // Dark when Telegram says so, or (no word) when its background is dark.
  const dark = scheme ? scheme === "dark" : bg ? isDarkRgb(bg) : false;
  const style = document.documentElement.style;
  for (const name of TOKEN_NAMES) style.removeProperty(name);
  applyTheme(dark ? TELEGRAM_DARK_BASE : TELEGRAM_LIGHT_BASE);
  for (const [name, value] of Object.entries(tokensForTheme(theme, readBaseHues()))) {
    style.setProperty(name, value);
  }
  // Telegram's own header and the space behind the page take the page's
  // colour, so its bar and the app's top row read as one.
  try {
    app?.setHeaderColor?.("bg_color");
    app?.setBackgroundColor?.("bg_color");
  } catch {
    // An older client without these: its default colours stand.
  }
}

/**
 * Follow Telegram's theme while the Mini App is mounted.
 *
 * The hook mounts before `AuthContext` has loaded Telegram's script (#2176):
 * until it lands the colours come from the launch address (no flash of
 * Tarii's own theme); once it does, the SDK's own colours, the header and
 * background calls, and `themeChanged` (`whenTelegramWebApp`).
 */
export function useTelegramTheme(): void {
  useEffect(() => {
    if (!telegramWebApp()) applyTelegramTheme(null);
    let app: TelegramWebApp | null = null;
    const onChange = () => applyTelegramTheme(app);
    const cancel = whenTelegramWebApp((loaded) => {
      app = loaded;
      applyTelegramTheme(loaded);
      loaded.onEvent?.("themeChanged", onChange);
    });
    return () => {
      cancel();
      app?.offEvent?.("themeChanged", onChange);
    };
  }, []);
}
